import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
/* eslint-disable no-nested-ternary */
import {
  Button,
  ButtonEmpty,
  FlexGroup,
  FlexItem,
  Flyout,
  FlyoutBody,
  FlyoutFooter,
  FlyoutHeader,
  notification,
  Text,
  Title,
} from '@antoree/ant-ui';
import { UNHAPPENED } from 'constants/session';
import { useToggle } from 'hooks';
import { FormattedMessage } from 'react-intl';
import {
  HomeworkFile,
  Session,
  useDeleteHomeworkFile,
  useRetrieveSessionContent,
  useSendHomeworkFile,
  useUpdateContent,
  RETRIEVE_SESSION_URL,
} from 'services';
import { API_SERVICE_URL } from 'configs/env.conf';
// S3 Upload - COMMENTED OUT (only using R2)
// import { deleteS3Video } from 'services/session/s3VideoUpload';
// import { deleteS3Videos } from 'services/session/s3VideoDeletion';
import { useQueryClient } from 'react-query';

import styles from '../Session.module.scss';
import SessionContentForm, { IFormData } from './SessionContentForm';
import { isAddedContent } from '../utils/sessionContent';
import { useScreenRecorder } from 'contexts/ScreenRecorderContext';
import HomeworkAssignmentModal from 'containers/Homework/HomeworkAssignmentModal';
import { useVideoUpload } from './hooks/useVideoUpload';
import { useAutoUploadRecordedVideo } from './hooks/useAutoUploadRecordedVideo';
import { videoStorage } from '../../../utils/videoStorage';

let globalLastHandledBlobUrl: string | null = null;

export type SessionContentProps = {
  session: Session;
  view: boolean;
  allowSubmit: boolean;
  onReload: (session: Session) => void;
  teacherId: number;
  hideTrigger?: boolean;
  autoUploadRecordedVideo?: boolean;
};

export type SessionContentHandle = {
  open: () => void;
};

const SessionContent = forwardRef<SessionContentHandle, SessionContentProps>(
  (
    {
      session,
      view,
      allowSubmit,
      onReload,
      teacherId,
      hideTrigger = false,
      autoUploadRecordedVideo = true,
    },
    ref,
  ) => {
    const { isCourseUnit: isUnit } = session;

    const idTest = [179266, 286234, 187737, 206144];
    const [isTriggerFile, setIsTriggerFiler] = useState(false);

    // Progress tracking state
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isAIHomeworkOpen, setIsAIHomeworkOpen] = useState(false);
    const [aiInitialFile, setAiInitialFile] = useState<File | null>(null);
    const [uploadedVideoFile, setUploadedVideoFile] = useState<File | null>(
      null,
    ); // Store uploaded video file
    // const aiFileInputRef = useRef<HTMLInputElement>(null);

    // Store videoId mapping for deletion (URL -> videoId)
    const [videoUrlToIdMap, setVideoUrlToIdMap] = useState<
      Record<string, number>
    >({});

    // ========== S3 Video URLs - COMMENTED OUT (only using R2) ==========
    // // Store S3 video URLs from complete responses for deletion
    // // Since S3 and R2 are independent, we store S3 URLs separately
    // // Use localStorage key based on session.id to persist across re-renders
    // const s3VideoUrlsStorageKey = `s3_video_urls_${session.id}`;
    //
    // const [s3VideoUrls, setS3VideoUrls] = useState<string[]>(() => {
    //   // Initialize from localStorage if available
    //   try {
    //     const stored = localStorage.getItem(s3VideoUrlsStorageKey);
    //     if (stored) {
    //       const parsed = JSON.parse(stored);
    //       return Array.isArray(parsed) ? parsed : [];
    //     }
    //   } catch (err) {
    //     // Silent error handling
    //   }
    //   return [];
    // });
    //
    // // Helper to update both state and localStorage ok
    // const updateS3VideoUrls = useCallback(
    //   (urls: string[] | ((prev: string[]) => string[])) => {
    //     setS3VideoUrls(prev => {
    //       const newUrls = typeof urls === 'function' ? urls(prev) : urls;
    //       try {
    //         localStorage.setItem(s3VideoUrlsStorageKey, JSON.stringify(newUrls));
    //       } catch (err) {
    //         // Silent error handling
    //       }
    //       return newUrls;
    //     });
    //   },
    //   [s3VideoUrlsStorageKey],
    // );
    // ========== END S3 Video URLs ==========

    const [videoInputKey, setVideoInputKey] = useState(0);
    const [homeworkInputKey, setHomeworkInputKey] = useState(0);
    const [isDeletingVideo, setIsDeletingVideo] = useState(false);

    const { isVisiable, close, open } = useToggle();

    useImperativeHandle(ref, () => ({
      open,
    }));

    const { recordedVideo, setRecordedVideo } = useScreenRecorder();

    // Use video upload hook
    const { uploadVideo } = useVideoUpload({
      sessionId: session.id,
      setUploadProgress,
      setIsUploading,
      setVideoInputKey,
      setVideoUrlToIdMap,
      // updateS3VideoUrls, // S3 Upload - COMMENTED OUT (only using R2)
    });

    const { data } = useRetrieveSessionContent(
      { id: session.id, isUnit },
      {
        enabled: isVisiable,
        cacheTime: 0,
      },
    );

    const {
      mutate: sendHomeworkFileMutaion,
      isLoading: isLoadingSendHomeworkFile,
    } = useSendHomeworkFile({
      onSuccess: () => {
        notification.success({
          title: <FormattedMessage defaultMessage="Upload file success" />,
        });
      },
      onError: () => {
        notification.error({
          title: <FormattedMessage defaultMessage="Upload file failed" />,
        });
      },
    });

    const queryClient = useQueryClient();

    // Tự động clear input khi danh sách homework files rỗng
    useEffect(() => {
      const homeworkUrls = data?.data?.homeworkUrl;
      if (homeworkUrls && homeworkUrls.length === 0) {
        setHomeworkInputKey(prev => prev + 1);
      }
    }, [data?.data?.homeworkUrl]);

    const handleSendHomework = (files: FileList | null) => {
      if (files && files[0]) {
        const file = files[0];

        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
          notification.error({
            title: <FormattedMessage defaultMessage="File quá lớn" />,
            text: (
              <FormattedMessage defaultMessage="Vui lòng chọn file dưới 10MB" />
            ),
          });
          return;
        }

        // Validate file is not empty
        if (file.size === 0) {
          notification.error({
            title: <FormattedMessage defaultMessage="File rỗng" />,
            text: (
              <FormattedMessage defaultMessage="Vui lòng chọn file có nội dung" />
            ),
          });
          return;
        }

        // Validate file type
        const allowedTypes = [
          'image/jpeg',
          'image/jpg',
          'image/png',
          'image/gif',
          'image/webp',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ];

        const allowedExtensions = [
          '.jpg',
          '.jpeg',
          '.png',
          '.gif',
          '.webp',
          '.pdf',
          '.doc',
          '.docx',
          '.ppt',
          '.pptx',
        ];

        const fileName = file.name.toLowerCase();
        const hasValidExtension = allowedExtensions.some(ext =>
          fileName.endsWith(ext),
        );
        const hasValidMimeType = file.type && allowedTypes.includes(file.type);

        if (!hasValidExtension && !hasValidMimeType) {
          notification.error({
            title: (
              <FormattedMessage defaultMessage="Định dạng file không được hỗ trợ" />
            ),
            text: (
              <FormattedMessage defaultMessage="Vui lòng chọn file: JPG, PNG, PDF, DOC, DOCX, PPT, PPTX" />
            ),
          });
          return;
        }

        const formData = new FormData();
        formData.append('attachment', file);
        if (isUnit) formData.append('is-unit', '1');

        setIsTriggerFiler(true);
        sendHomeworkFileMutaion({
          id: session.id,
          courseId: session.course_id,
          formData,
        });

        // ========== Auto-trigger AI Homework from FILE - REMOVED ==========
        // Now we only generate homework from VIDEO after save
        // setAiInitialFile(file);
        // setIsAIHomeworkOpen(true);
        // ========== END ==========
      }
    };

    // Validate video file format
    const validateVideoFile = (file: File): boolean => {
      const allowedExtensions = [
        '.mp4',
        '.avi',
        '.mkv',
        '.webm',
        '.3gp',
        '.ogv',
        '.flv',
      ];
      const allowedMimeTypes = [
        'video/mp4',
        'video/x-msvideo', // avi
        'video/x-matroska', // mkv
        'video/webm',
        'video/3gpp', // 3gp
        'video/ogg', // ogv
        'video/x-flv', // flv
      ];

      // Check by file extension
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext =>
        fileName.endsWith(ext),
      );

      // Check by MIME type
      const hasValidMimeType =
        Boolean(file.type) && allowedMimeTypes.includes(file.type);

      return hasValidExtension || hasValidMimeType;
    };

    const handleVideoAttachment = useCallback(
      async (videos: FileList | null) => {
        if (videos && videos[0]) {
          const videoFile = videos[0];

          // Validate video file format
          if (!validateVideoFile(videoFile)) {
            notification.error({
              title: <FormattedMessage defaultMessage="Invalid video format" />,
              text: (
                <FormattedMessage defaultMessage="Please upload a video file with one of these formats: mp4, avi, mkv, webm, 3gp, ogv, flv" />
              ),
            });
            setVideoInputKey(prev => prev + 1); // Reset input
            return;
          }

          setIsTriggerFiler(true);
          setIsUploading(true);
          setUploadProgress(0);

          // Store video file for AI homework generation later
          setUploadedVideoFile(videoFile);

          // Use the upload hook
          try {
            await uploadVideo(videoFile);
          } catch (error) {
            console.error('Video upload failed:', error);
            setIsUploading(false);
            setUploadProgress(0);
            setUploadedVideoFile(null); // Clear on error
            throw error; // Propagate error for status tracking
          }
        }
      },
      [
        setIsTriggerFiler,
        setIsUploading,
        setUploadProgress,
        setVideoInputKey,
        uploadVideo,
      ],
    );

    // Auto-upload recorded video when recording stops
    useAutoUploadRecordedVideo({
      recordedVideo: autoUploadRecordedVideo ? recordedVideo : null,
      sessionId: session.id,
      onUpload: handleVideoAttachment,
      onOpenForm: open,
    });

    const deleteHomeworkFileMutation = useDeleteHomeworkFile({
      onSuccess: async (response, variables, context) => {
        // Reset input ngay lập tức
        setHomeworkInputKey(prev => prev + 1);

        // Refetch data để cập nhật danh sách
        await queryClient.refetchQueries([RETRIEVE_SESSION_URL(session.id)]);

        notification.success({
          title: (
            <FormattedMessage defaultMessage="File deleted successfully" />
          ),
        });
      },
      onError: () => {
        notification.error({
          title: <FormattedMessage defaultMessage="Delete file failed" />,
        });
      },
    });

    const handleDeleteFile = (file: HomeworkFile) => {
      const index = data?.data.homeworkUrl.findIndex(item => item === file);
      if (index !== -1 && index !== undefined) {
        deleteHomeworkFileMutation.mutate({
          id: session.id,
          courseId: session.course_id,
          indices: [index],
          isUnit,
        });
      }
    };

    const handleDeleteVideo = async (videoUrlOrIndex: string | number) => {
      setIsDeletingVideo(true);
      setIsUploading(false);
      setUploadProgress(0);

      // Handle both URL string and index number
      let videoUrl: string | undefined;
      if (typeof videoUrlOrIndex === 'string') {
        videoUrl = videoUrlOrIndex;
      } else {
        // If it's a number (index), get URL from data
        const videoUrls = data?.data.videoUrl;
        if (Array.isArray(videoUrls)) {
          videoUrl = videoUrls[videoUrlOrIndex];
        } else if (videoUrls && typeof videoUrls === 'object') {
          // Convert object to array and get by index
          const videoArray = Object.values(videoUrls).filter(
            url => typeof url === 'string',
          ) as string[];
          videoUrl = videoArray[videoUrlOrIndex];
        }
      }

      const token = localStorage.getItem('token');

      if (videoUrl) {
        try {
          // ========== S3 Delete - COMMENTED OUT (only using R2) ==========
          // // Check if it's an S3 URL (contains s3.amazonaws.com or s3 bucket pattern)
          // const isS3Url =
          //   (typeof videoUrl === 'string' &&
          //     videoUrl.includes('s3.amazonaws.com')) ||
          //   (typeof videoUrl === 'string' && videoUrl.includes('.s3.')) ||
          //   (typeof videoUrl === 'string' &&
          //     /https?:\/\/[^/]+\.s3[^/]*\.amazonaws\.com/.test(videoUrl));
          //
          // if (isS3Url) {
          //   // Delete from S3
          //   const result = await deleteS3Video({
          //     unitId: Number(session.id),
          //     videoUrl: videoUrl as string,
          //   });
          //
          //   notification.success({
          //     title: <FormattedMessage defaultMessage="Video deleted" />,
          //   });
          //
          //   // Clear from map if it exists
          //   setVideoUrlToIdMap(prev => {
          //     const newMap = { ...prev };
          //     delete newMap[videoUrl as string];
          //     return newMap;
          //   });
          //
          //   // Update cache with the new list returned by the server
          //   if (result.unitVideo) {
          //     queryClient.setQueryData(
          //       [RETRIEVE_SESSION_URL(session.id)],
          //       (old: any) => {
          //         if (!old) return old;
          //         return {
          //           ...old,
          //           data: {
          //             ...old.data,
          //             videoUrl: result.unitVideo,
          //           },
          //         };
          //       },
          //     );
          //   }
          //   // Always refetch to ensure fresh data
          //   await queryClient.refetchQueries([RETRIEVE_SESSION_URL(session.id)]);
          //   setVideoInputKey(prev => prev + 1); // Clear input on delete
          // } else {
          // ========== END S3 Delete ==========

          // Delete from R2
          const response = await fetch(
            `${API_SERVICE_URL}/video-upload/delete`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                unitId: Number(session.id),
                videoUrl: videoUrl,
              }),
            },
          );

          if (response.ok) {
            const result = await response.json();

            // ========== S3 Videos deletion - COMMENTED OUT ==========
            // // Also delete all S3 videos that were uploaded (if any)
            // // S3 videos are stored from S3 complete responses
            // if (s3VideoUrls.length > 0) {
            //   try {
            //     await deleteS3Videos({
            //       unitId: Number(session.id),
            //       s3VideoUrls: s3VideoUrls,
            //     });
            //     // Clear all S3 URLs after successful deletion
            //     updateS3VideoUrls([]);
            //   } catch (err) {
            //     console.warn('Failed to delete S3 videos:', err);
            //     // Don't throw - R2 deletion was successful, S3 deletion failure is non-critical
            //   }
            // }
            // ========== END S3 Videos deletion ==========

            notification.success({
              title: <FormattedMessage defaultMessage="Video deleted" />,
            });

            // Clear from map if it exists
            setVideoUrlToIdMap(prev => {
              const newMap = { ...prev };
              delete newMap[videoUrl as string];
              return newMap;
            });

            // Update cache with the new list returned by the server
            if (result.data?.unitVideo) {
              queryClient.setQueryData(
                [RETRIEVE_SESSION_URL(session.id)],
                (old: any) => {
                  if (!old) return old;
                  return {
                    ...old,
                    data: {
                      ...old.data,
                      videoUrl: result.data.unitVideo,
                    },
                  };
                },
              );
            }
            // Always refetch to ensure fresh data
            await queryClient.refetchQueries([
              RETRIEVE_SESSION_URL(session.id),
            ]);
            setVideoInputKey(prev => prev + 1); // Clear input on delete
          } else {
            const errorData = await response.json();
            notification.error({
              title: <FormattedMessage defaultMessage="Delete video failed" />,
              text: errorData.message || '',
            });
          }
          // } // Removed closing bracket for commented S3 if/else
        } catch (error) {
          console.error('Error deleting video:', error);
          notification.error({
            title: <FormattedMessage defaultMessage="Delete video failed" />,
            text: error instanceof Error ? error.message : 'Unknown error',
          });
        } finally {
          setIsDeletingVideo(false);
        }
      } else {
        setIsDeletingVideo(false);
      }
    };

    const { mutate, isLoading } = useUpdateContent({
      onSuccess: async response => {
        onReload(response?.data.session);
        notification.success({
          title: (
            <FormattedMessage defaultMessage="Update content successfully" />
          ),
        });
        setIsTriggerFiler(false);
        close();

        // Clear ALL videos for this session from IndexedDB logic is removed
        if (recordedVideo) {
          // Clear recorded video after successful save
          setRecordedVideo(null);
        }

        // ========== Trigger AI Homework from video after save ==========
        // Use the uploaded video file if available
        if (uploadedVideoFile) {
          setAiInitialFile(uploadedVideoFile);
          setIsAIHomeworkOpen(true);
        }
        // ========== END ==========
      },
      onError: err => {
        const mesError = err?.response?.data?.errors[0]?.message;
        notification.error({
          title: <FormattedMessage defaultMessage="Update content failed" />,
          text: `${mesError}`,
        });
      },
    });

    const handleSubmit = (formData: IFormData) => {
      mutate({
        id: session.id,
        courseId: session.course_id,
        ...formData,
        isUnit,
      });
    };

    const handleCloseFlyout = async () => {
      close();
      setIsTriggerFiler(false);
      setIsUploading(false);
      setUploadProgress(0);
      setAiInitialFile(null);
      setUploadedVideoFile(null);

      // (User canceled, video was not saved to session content)
      // Deletion from local storage removed here based on user request.
      if (recordedVideo) {
        // Clear recorded video when closing flyout without saving
        setRecordedVideo(null);
      }
    };

    const isNotHappened = session.happenedStatus === UNHAPPENED;

    return (
      <>
        {!hideTrigger &&
          (!isAddedContent(session) ? (
            <ButtonEmpty onClick={() => open()} className="h-6">
              <span>
                <FormattedMessage
                  id="session_content.header.add"
                  defaultMessage="Add content"
                />
              </span>
              <span className="text-danger"> *</span>
            </ButtonEmpty>
          ) : (
            <ButtonEmpty onClick={() => open()} className="h-6">
              <span>
                <FormattedMessage
                  id="session_content.header.added"
                  defaultMessage="Content added"
                />
              </span>
            </ButtonEmpty>
          ))}

        {session?.testRequired && (
          <Text className="text-center">
            <p>
              <FormattedMessage
                id="session_content.test_duration"
                defaultMessage="{testDuration}th-hour TEST"
                values={{ testDuration: session?.testDuration }}
              />
            </p>
          </Text>
        )}

        {isVisiable && (
          <Flyout
            className={styles.sessionContentFlyout}
            ownFocus
            outsideClickCloses={false}
            onClose={handleCloseFlyout}
          >
            <FlyoutHeader>
              <Title size="m">
                <h2>
                  <FormattedMessage
                    id="session_content.header"
                    defaultMessage="Session content"
                  />
                </h2>
              </Title>
            </FlyoutHeader>
            <FlyoutBody>
              <SessionContentForm
                sessionContent={data?.data}
                view={view}
                allowSubmit
                onReload={() => {}}
                onSubmit={handleSubmit}
                onSendHomework={handleSendHomework}
                onSendHomeworkVideo={handleVideoAttachment}
                isLoadingSendHomeworkFile={isLoadingSendHomeworkFile}
                isLoadingSendHomeworkVideo={isUploading}
                onDeleteFile={handleDeleteFile}
                onDeleteVideo={handleDeleteVideo}
                isLoadingDeleteVideo={isDeletingVideo}
                isTriggerFile={isTriggerFile}
                isNotHappened={isNotHappened}
                isLoading={isLoading}
                isTest={idTest.includes(teacherId)}
                uploadProgress={uploadProgress}
                isUploading={isUploading}
                videoInputKey={videoInputKey}
                homeworkInputKey={homeworkInputKey}
              />
            </FlyoutBody>
            <FlyoutFooter className={styles.sessionContentFlyoutFooter}>
              <FlexGroup
                justifyContent={allowSubmit ? 'spaceBetween' : 'flexStart'}
              >
                <FlexItem grow={false}>
                  <ButtonEmpty onClick={handleCloseFlyout}>
                    {allowSubmit ? (
                      <FormattedMessage
                        id="common.cancel"
                        defaultMessage="Cancel"
                      />
                    ) : (
                      <FormattedMessage
                        id="common.close"
                        defaultMessage="Close"
                      />
                    )}
                  </ButtonEmpty>
                </FlexItem>
              </FlexGroup>
            </FlyoutFooter>
          </Flyout>
        )}

        <HomeworkAssignmentModal
          isOpen={isAIHomeworkOpen}
          onClose={() => {
            setIsAIHomeworkOpen(false);
            setAiInitialFile(null);
          }}
          unitId={session.id}
          studentId={
            (session.student as any)?.user_id || (session.student as any)?.id
          }
          teacherId={teacherId}
          initialFile={aiInitialFile}
          onSuccess={hwId => {
            // Success callback if needed
            notification.success({
              title: (
                <FormattedMessage
                  id="homework.ai_assign_success"
                  defaultMessage="AI Homework assigned successfully"
                />
              ),
            });
          }}
        />
      </>
    );
  },
);

export default SessionContent;
